import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Segment } from '../../interfaces/accounting.interface';
import { CashBySegment, InterSegmentReconciliation as Matrix } from '../../interfaces/interSegment.interface';
import { InterSegmentService } from '../../services/interSegment.service';
import { SegmentService } from '../../services/segment.service';
import { AuthService } from '../../services/auth.service';
import SearchableSelect from '../common/SearchableSelect';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { toLocalIsoDate } from '../../utils/dateUtils';
import { formatMoney } from '../../utils/format';

interface AllocDraft { lender: string; borrower: string; amount: string; date: string }

/**
 * Inter-segment reconciliation matrix: each lender x borrower cell compares due-from (lender, counterparty
 * borrower) with due-to (borrower, counterparty lender). Below it: pair-account lines with no counterparty
 * (usually manual journals), the clearing balance per segment with "Allocate residual to pair", and the cash
 * held per segment.
 */
const InterSegmentReconciliation: React.FC = () => {
    const toast = useToast();
    const canManage = usePermission(PERMISSIONS.SEGMENT_MANAGE);
    const institutionId = AuthService.getSelectedInstitution()!;
    const [asOf, setAsOf] = useState(toLocalIsoDate());
    const [matrix, setMatrix] = useState<Matrix | null>(null);
    const [cash, setCash] = useState<CashBySegment | null>(null);
    const [segments, setSegments] = useState<Segment[]>([]);
    const [alloc, setAlloc] = useState<AllocDraft | null>(null);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setMatrix(await InterSegmentService.reconciliation(institutionId, asOf));
            // Cash needs bank-account view as well; a caller without it still gets the matrix.
            setCash(await InterSegmentService.cashBySegment(institutionId, asOf).catch(() => null));
        } catch (e) {
            toast.showApiError(e, 'Failed to load the reconciliation');
        } finally {
            setLoading(false);
        }
    }, [institutionId, asOf]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        SegmentService.getByInstitution().then(p => setSegments(p.content ?? [])).catch(() => setSegments([]));
    }, []);

    const cell = (lender: string, borrower: string) =>
        matrix?.cells.find(c => c.lenderSegmentId === lender && c.borrowerSegmentId === borrower);

    const clearingOf = (segmentId: string) =>
        Number(matrix?.clearing.find(c => c.segmentId === segmentId)?.netDebit ?? 0);

    const suggestAmount = (lender: string, borrower: string): string => {
        const owed = clearingOf(lender);          // lender is owed (net debit)
        const owes = -clearingOf(borrower);       // borrower owes (net credit)
        const v = Math.min(owed, owes);
        return v > 0 ? v.toFixed(2) : '';
    };

    const submit = async () => {
        if (!alloc) return;
        setBusy(true);
        try {
            await InterSegmentService.allocateResidual({
                institutionId, lenderSegmentId: alloc.lender, borrowerSegmentId: alloc.borrower,
                amount: Number(alloc.amount), date: alloc.date,
            });
            toast.showSuccess('Residual allocated to the pair');
            setAlloc(null);
            load();
        } catch (e) {
            toast.showApiError(e, 'Failed to allocate the residual');
        } finally {
            setBusy(false);
        }
    };

    const allocValid = !!alloc && !!alloc.lender && !!alloc.borrower && alloc.lender !== alloc.borrower
        && Number(alloc.amount) > 0 && !!alloc.date;

    return (
        <Box data-help-page="accounting.interSegRecon.page" sx={{ p: { xs: 1.5, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5">Inter-segment reconciliation</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Button data-help="accounting.interSegRecon.loans" component={RouterLink} to="/accounting/inter-segment/loans" variant="outlined">Loans</Button>
                    <Button data-help="accounting.interSegRecon.eliminations" component={RouterLink} to="/accounting/inter-segment/elimination" variant="outlined">Eliminations</Button>
                </Box>
            </Box>
            <Box data-help-section="accounting.interSegRecon.filters" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                <TextField data-help="accounting.interSegRecon.asOf" label="As of" type="date" size="small" value={asOf} InputLabelProps={{ shrink: true }}
                    onChange={e => setAsOf(e.target.value)} sx={{ flex: '1 1 160px', maxWidth: 200 }} />
                {matrix && (
                    <Chip color={matrix.allMatched ? 'success' : 'warning'}
                        label={matrix.allMatched ? 'All pairs agree' : 'Differences to resolve'} />
                )}
            </Box>

            <Typography variant="subtitle1">Due-from (lender row) vs due-to (borrower column)</Typography>
            <TableContainer component={Paper}>
                <Table data-help-section="accounting.interSegRecon.matrix" size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="accounting.interSegRecon.matrix.col.pair">Lender \ Borrower</TableCell>
                            {(matrix?.segments ?? []).map(s => <TableCell key={s.id} align="right">{s.name}</TableCell>)}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(matrix?.segments ?? []).map(lender => (
                            <TableRow key={lender.id}>
                                <TableCell><b>{lender.name}</b></TableCell>
                                {(matrix?.segments ?? []).map(borrower => {
                                    const c = lender.id === borrower.id ? undefined : cell(lender.id, borrower.id);
                                    return (
                                        <TableCell key={borrower.id} align="right">
                                            {lender.id === borrower.id ? '—' : c ? (
                                                <Box>
                                                    <div>{formatMoney(c.dueFrom)} / {formatMoney(c.dueTo)}</div>
                                                    <Chip size="small" color={c.matched ? 'success' : 'error'}
                                                        label={c.matched ? 'Matched' : `Diff ${formatMoney(c.difference)}`} />
                                                </Box>
                                            ) : ''}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                        {matrix && matrix.segments.length === 0 && (
                            <TableRow><TableCell align="center">No inter-segment balances yet.</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="subtitle1">Unassigned lines (no counterparty)</Typography>
            {matrix && matrix.unassigned.length > 0 && (
                <Alert severity="warning">
                    These due-from / due-to lines carry no counterparty segment (typically manual journals), so they cannot be
                    eliminated or matched. Reverse and repost them through an inter-segment loan.
                </Alert>
            )}
            <TableContainer component={Paper}>
                <Table data-help-section="accounting.interSegRecon.unassigned" size="small">
                    <TableHead>
                        <TableRow><TableCell data-help="accounting.interSegRecon.unassigned.col.account">Account</TableCell><TableCell data-help="accounting.interSegRecon.unassigned.col.segment">Segment</TableCell><TableCell data-help="accounting.interSegRecon.unassigned.col.net" align="right">Net debit</TableCell></TableRow>
                    </TableHead>
                    <TableBody>
                        {(matrix?.unassigned ?? []).map(u => (
                            <TableRow key={`${u.accountId}-${u.segmentId}`}>
                                <TableCell>{u.accountCode} - {u.accountName}</TableCell>
                                <TableCell>{u.segmentName}</TableCell>
                                <TableCell align="right">{formatMoney(u.netDebit)}</TableCell>
                            </TableRow>
                        ))}
                        {matrix && matrix.unassigned.length === 0 && (
                            <TableRow><TableCell colSpan={3} align="center">None.</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle1">Clearing balance per segment</Typography>
                {canManage && (
                    <Button data-help="accounting.interSegRecon.allocate" variant="outlined" onClick={() => setAlloc({ lender: '', borrower: '', amount: '', date: toLocalIsoDate() })}>
                        Allocate residual to pair
                    </Button>
                )}
            </Box>
            <TableContainer component={Paper}>
                <Table data-help-section="accounting.interSegRecon.clearing" size="small">
                    <TableHead>
                        <TableRow><TableCell data-help="accounting.interSegRecon.clearing.col.segment">Segment</TableCell><TableCell data-help="accounting.interSegRecon.clearing.col.net" align="right">Net debit (owed to the segment)</TableCell></TableRow>
                    </TableHead>
                    <TableBody>
                        {(matrix?.clearing ?? []).map(c => (
                            <TableRow key={c.segmentId ?? 'none'}>
                                <TableCell>{c.segmentName}</TableCell>
                                <TableCell align="right">{formatMoney(c.netDebit)}</TableCell>
                            </TableRow>
                        ))}
                        {matrix && matrix.clearing.length === 0 && (
                            <TableRow><TableCell colSpan={2} align="center">No clearing residual.</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {cash && (
                <>
                    <Typography variant="subtitle1">Cash and bank by segment</Typography>
                    <TableContainer component={Paper}>
                        <Table data-help-section="accounting.interSegRecon.cash" size="small">
                            <TableHead><TableRow><TableCell data-help="accounting.interSegRecon.cash.col.segment">Segment</TableCell><TableCell data-help="accounting.interSegRecon.cash.col.balance" align="right">Balance</TableCell></TableRow></TableHead>
                            <TableBody>
                                {cash.segments.map(s => (
                                    <TableRow key={s.segmentId ?? 'none'}>
                                        <TableCell>{s.segmentName}</TableCell>
                                        <TableCell align="right">{formatMoney(s.balance)}</TableCell>
                                    </TableRow>
                                ))}
                                <TableRow>
                                    <TableCell><b>Total</b></TableCell>
                                    <TableCell align="right"><b>{formatMoney(cash.total)}</b></TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
            {loading && <Typography color="text.secondary">Loading…</Typography>}

            <Dialog data-help-page="accounting.interSegRecon.allocate.page" open={!!alloc} onClose={() => setAlloc(null)} fullWidth maxWidth="sm">
                <DialogTitle>Allocate clearing residual to a pair</DialogTitle>
                {alloc && (
                    <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            The lender segment (owed money in clearing) records a due-from the borrower; the borrower segment
                            (owing in clearing) records a due-to the lender. Clearing is reduced on both sides.
                        </Typography>
                        <SearchableSelect data-help="accounting.interSegRecon.allocate.field.lender" options={segments} value={alloc.lender} label="Lender segment"
                            onChange={id => setAlloc({ ...alloc, lender: id, amount: suggestAmount(id, alloc.borrower) })}
                            getOptionLabel={s => `${s.code} — ${s.name}`} />
                        <SearchableSelect data-help="accounting.interSegRecon.allocate.field.borrower" options={segments.filter(s => s.id !== alloc.lender)} value={alloc.borrower}
                            label="Borrower segment"
                            onChange={id => setAlloc({ ...alloc, borrower: id, amount: suggestAmount(alloc.lender, id) })}
                            getOptionLabel={s => `${s.code} — ${s.name}`} />
                        <TextField data-help="accounting.interSegRecon.allocate.field.amount" label="Amount" type="number" value={alloc.amount}
                            onChange={e => setAlloc({ ...alloc, amount: e.target.value })} inputProps={{ min: 0, step: '0.01' }} />
                        <TextField data-help="accounting.interSegRecon.allocate.field.date" label="Date" type="date" value={alloc.date} InputLabelProps={{ shrink: true }}
                            onChange={e => setAlloc({ ...alloc, date: e.target.value })} />
                    </DialogContent>
                )}
                <DialogActions>
                    <Button data-help="accounting.interSegRecon.allocate.cancel" onClick={() => setAlloc(null)}>Cancel</Button>
                    <Button data-help="accounting.interSegRecon.allocate.submit" variant="contained" onClick={submit} disabled={!allocValid || busy}>Allocate</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default InterSegmentReconciliation;
